import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MdArrowBack, MdCheckCircle, MdShoppingCart, MdClose, MdLocalShipping } from 'react-icons/md';
import { EUROASIANN_OFFER_LABEL } from '@euroasiann/shared';
import { authenticatedFetch, resolveFileUrl } from '../../lib/api';
import { cn } from '../../lib/utils';
import { useCurrency } from '../../context/CurrencyContext';
import { FormattedAmount } from '../../components/shared/FormattedAmount';
import { FileDownloadLink } from '../../components/shared/FileDownloadLink';
import { useState } from 'react';
import React from 'react';
import {
  CREDIT_TYPE_OPTIONS,
  PAYMENT_TERM_OPTIONS,
  PAYMENT_TERM_CUSTOM_VALUE,
  INSURANCE_TERM_OPTIONS,
  TAX_TERM_OPTIONS,
  TRANSPORT_TERM_OPTIONS,
  DELIVERY_TERM_OPTIONS,
  PACKING_TERM_OPTIONS,
  advancePercentageForPaymentTerm,
  isPresetPaymentTermOption,
} from '../../constants/quotationTerms';
import { PageSkeleton } from '@euroasiann/shared/ui';

const specialOfferFieldClass =
  'w-full p-2 border rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] border-[hsl(var(--border))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]';
const specialOfferTableInputClass =
  'w-full p-1 border rounded bg-[hsl(var(--background))] text-[hsl(var(--foreground))] border-[hsl(var(--border))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))]';

interface RFQDetails {
  _id: string;
  rfqNumber: string;
  title: string;
  description?: string;
  vesselId?: {
    _id: string;
    name: string;
    imoNumber?: string;
    hullNumber?: string;
    serialNumber?: string;
    type?: string;
  };
  supplyPort?: string;
  brand?: string;
  status: string;
  createdAt: string;
  metadata?: Record<string, any>;
}

interface RFQItem {
  impaNo?: string;
  description: string;
  partNo?: string;
  altPartNo?: string;
  positionNo?: string;
  dimensions?: string;
  requiredQty: number | string;
  uom?: string;
  remark?: string;
}

interface QuotationItem {
  description: string;
  requiredQty: number;
  quotedPrice: number | string;
  offeredQty: number | string;
  offeredQuality: string;
}

interface QuotationTerms {
  creditType?: string;
  paymentTerm?: string;
  insuranceTerm?: string;
  taxTerm?: string;
  transportTerm?: string;
  deliveryTerm?: string;
  packingTerm?: string;
  currency?: string;
  advancePercentage?: number;
}

interface SubmittedQuotation {
  _id: string;
  quotationNumber: string;
  title: string;
  description?: string;
  status: string;
  totalAmount: number;
  currency: string;
  submittedAt: string;
  items: QuotationItem[];
  terms: QuotationTerms;
  isAdminOffer?: boolean;
  vendorOrganizationId?: {
    _id: string;
    name: string;
  };
}

export function RFQDetailsPage({
  rfqId: rfqIdProp,
  backTo = '/dashboard/admin/rfqs',
  embedded = false,
}: {
  rfqId?: string;
  backTo?: string;
  embedded?: boolean;
} = {}) {
  const { id: idFromParams } = useParams<{ id: string }>();
  const id = rfqIdProp || idFromParams;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currency: portalCurrency } = useCurrency();
  const [showQuotationForm, setShowQuotationForm] = useState(false);
  const [quotationItems, setQuotationItems] = useState<QuotationItem[]>([]);
  const [quotationTerms, setQuotationTerms] = useState<QuotationTerms>({
    currency: portalCurrency,
  });
  const [paymentTermCustomMode, setPaymentTermCustomMode] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const goBack = () => navigate(backTo);

  const { data: rfqDetails, isLoading, error } = useQuery<RFQDetails>({
    queryKey: ['admin-rfq-details', id],
    queryFn: async () => {
      if (!id) throw new Error('RFQ ID is required');
      const response = await authenticatedFetch(`/api/v1/admin/rfq/${id}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to fetch RFQ details' }));
        throw new Error(errorData.error || 'Failed to fetch RFQ details');
      }
      const data = await response.json();
      return data.data;
    },
    enabled: !!id,
    retry: 1,
  });

  // Extract RFQ items from metadata
  const rfqItems: RFQItem[] = React.useMemo(() => {
    if (!rfqDetails) return [];
    const items = rfqDetails.metadata?.items || [];
    if (Array.isArray(items) && items.length > 0) {
      return items.map((item: any) => {
        // Parse requiredQuantity - handle both string and number, and empty values
        let requiredQty: number | string = '-';
        const requiredQuantity = item.requiredQuantity || item.requiredQty;
        if (requiredQuantity !== undefined && requiredQuantity !== null && requiredQuantity !== '') {
          const parsed = typeof requiredQuantity === 'string' 
            ? parseFloat(requiredQuantity) 
            : Number(requiredQuantity);
          if (!isNaN(parsed) && parsed >= 0) {
            requiredQty = parsed;
          }
        }
        
        return {
          impaNo: item.impaNo || '-',
          description: item.itemDescription || item.description || '-',
          partNo: item.partNo || '-',
          altPartNo: item.altPartNo || '-',
          positionNo: item.positionNo || '-',
          dimensions: item.dimensions || '-',
          requiredQty: requiredQty,
          uom: item.uom || '-',
          remark: item.generalRemark || item.remark || '-',
        };
      });
    }
    return [];
  }, [rfqDetails]);

  // Initialize quotation items from RFQ items
  React.useEffect(() => {
    if (rfqItems.length > 0 && quotationItems.length === 0) {
      setQuotationItems(
        rfqItems.map((item) => {
          // Parse requiredQty - convert string numbers to numbers, handle '-' and empty values
          let requiredQtyNum = 0;
          if (typeof item.requiredQty === 'number') {
            requiredQtyNum = item.requiredQty;
          } else if (typeof item.requiredQty === 'string' && item.requiredQty !== '-' && item.requiredQty !== '') {
            const parsed = parseFloat(item.requiredQty);
            if (!isNaN(parsed) && parsed >= 0) {
              requiredQtyNum = parsed;
            }
          }
          
          return {
            description: item.description,
            requiredQty: requiredQtyNum,
            quotedPrice: '', // Start blank
            offeredQty: requiredQtyNum > 0 ? requiredQtyNum : '', // Pre-fill only if there's a required quantity
            offeredQuality: 'Standard',
          };
        })
      );
    }
  }, [rfqItems]);

  // Check if admin quotation already exists for this RFQ
  const { data: quotations } = useQuery<SubmittedQuotation[]>({
    queryKey: ['admin-quotations', id],
    queryFn: async () => {
      if (!id) return [];
      try {
        const response = await authenticatedFetch(`/api/v1/admin/quotation/rfq/${id}`);
        if (!response.ok) {
          return [];
        }
        const data = await response.json();
        return Array.isArray(data.data) ? data.data : [];
      } catch {
        return [];
      }
    },
    enabled: !!id,
  });

  const existingQuotation = React.useMemo(() => {
    return quotations?.find(q => q.isAdminOffer) || null;
  }, [quotations]);

  const vendorQuotations = React.useMemo(() => {
    return quotations?.filter(q => !q.isAdminOffer) || [];
  }, [quotations]);

  // Fetch payment proof for this quotation
  const { data: paymentProof, isLoading: isLoadingPaymentProof } = useQuery<any>({
    queryKey: ['admin-payment-proof', existingQuotation?._id],
    queryFn: async () => {
      if (!existingQuotation?._id) return null;
      try {
        const response = await authenticatedFetch(`/api/v1/admin/payment-proof/quotation/${existingQuotation._id}`);
        if (!response.ok) {
          return null;
        }
        const data = await response.json();
        return data.data || null;
      } catch (error) {
        console.error('Error fetching payment proof:', error);
        return null;
      }
    },
    enabled: !!existingQuotation?._id,
  });

  const { data: linkedPo } = useQuery<any>({
    queryKey: ['admin-rfq-po', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await authenticatedFetch(`/api/v1/admin/rfq/${id}/purchase-order`);
      if (!response.ok) return null;
      const data = await response.json();
      return data.data || null;
    },
    enabled: !!id && existingQuotation?.status === 'finalized',
  });

  const { data: linkedShipment } = useQuery<any>({
    queryKey: ['admin-rfq-shipment', linkedPo?.poNumber],
    queryFn: async () => {
      if (!linkedPo?.poNumber) return null;
      const response = await authenticatedFetch(
        `/api/v1/admin/shipments?search=${encodeURIComponent(linkedPo.poNumber)}`
      );
      if (!response.ok) return null;
      const data = await response.json();
      const list = data.data?.shipments || [];
      return list.find((s: any) => s.orderNumber === linkedPo.poNumber) || list[0] || null;
    },
    enabled: !!linkedPo?.poNumber && paymentProof?.status === 'approved',
  });

  const submitQuotationMutation = useMutation({
    mutationFn: async (quotationData: any) => {
      const response = await authenticatedFetch('/api/v1/admin/quotation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quotationData),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to submit quotation');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-rfq-details', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-rfqs'] });
      queryClient.invalidateQueries({ queryKey: ['admin-quotations', id] });
      setShowQuotationForm(false);
      alert('Special offer submitted successfully!');
    },
  });

  const handleSubmitQuotation = () => {
    if (!rfqDetails || !id) return;
    
    if (quotationItems.length === 0) {
      alert('Please add at least one quotation item');
      return;
    }

    const totalAmount = quotationItems.reduce(
      (sum, item) => {
        const price = typeof item.quotedPrice === 'number' ? item.quotedPrice : (parseFloat(String(item.quotedPrice)) || 0);
        const qty = typeof item.offeredQty === 'number' ? item.offeredQty : (parseInt(String(item.offeredQty)) || 0);
        return sum + price * qty;
      },
      0
    );

    // Transform items for backend - convert empty strings to 0
    const transformedItems = quotationItems.map((item) => ({
      ...item,
      quotedPrice: typeof item.quotedPrice === 'string' && item.quotedPrice === '' 
        ? 0 
        : (typeof item.quotedPrice === 'number' ? item.quotedPrice : parseFloat(String(item.quotedPrice)) || 0),
      offeredQty: typeof item.offeredQty === 'string' && item.offeredQty === '' 
        ? 0 
        : (typeof item.offeredQty === 'number' ? item.offeredQty : parseInt(String(item.offeredQty)) || 0),
    }));

    submitQuotationMutation.mutate({
      rfqId: id,
      title: title || `${EUROASIANN_OFFER_LABEL} for ${rfqDetails.rfqNumber}`,
      description: description || '',
      status: 'submitted',
      totalAmount,
      currency: quotationTerms.currency || 'USD',
      items: transformedItems,
      metadata: {
        terms: quotationTerms,
        items: quotationItems, // Keep original format in metadata
      },
    });
  };

  const updateQuotationItem = (index: number, field: keyof QuotationItem, value: any) => {
    setQuotationItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  if (isLoading) {
    return <PageSkeleton variant="cards" />;
  }

  if (error || !rfqDetails) {
    return (
      <div className={cn('w-full flex items-center justify-center', embedded ? 'min-h-[40vh]' : 'min-h-screen')}>
        <div className="text-center">
          <p className="text-red-500 mb-4">Error loading RFQ details</p>
          <button
            type="button"
            onClick={goBack}
            className="px-4 py-2 bg-[#0088cc] text-white rounded-lg hover:bg-[#0077b3]"
          >
            Back to RFQs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('w-full', embedded ? '' : 'min-h-screen p-8')}>
      <div className="mb-6">
        <button
          type="button"
          onClick={goBack}
          className="flex items-center gap-2 text-[hsl(var(--foreground))] hover:text-[hsl(var(--primary))] mb-4"
        >
          <MdArrowBack className="w-5 h-5" />
          Back to RFQs
        </button>
        <h1 className="text-3xl font-bold text-[hsl(var(--foreground))]">RFQ Details</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">RFQ Number: {rfqDetails.rfqNumber}</p>
      </div>

      <div className="space-y-6">
        {/* RFQ Information */}
        <div className="bg-[hsl(var(--card))] rounded-lg border border-[hsl(var(--border))] p-6">
          <h2 className="text-xl font-semibold mb-4">RFQ Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[hsl(var(--muted-foreground))]">Title</label>
              <p className="text-[hsl(var(--foreground))]">{rfqDetails.title}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-[hsl(var(--muted-foreground))]">Status</label>
              <p className="text-[hsl(var(--foreground))]">{rfqDetails.status}</p>
            </div>
            {rfqDetails.supplyPort && (
              <div>
                <label className="text-sm font-medium text-[hsl(var(--muted-foreground))]">Supply Port</label>
                <p className="text-[hsl(var(--foreground))]">{rfqDetails.supplyPort}</p>
              </div>
            )}
            {rfqDetails.brand && (
              <div>
                <label className="text-sm font-medium text-[hsl(var(--muted-foreground))]">Brand</label>
                <p className="text-[hsl(var(--foreground))]">{rfqDetails.brand}</p>
              </div>
            )}
          </div>
        </div>

        {/* Vessel Information */}
        {rfqDetails.vesselId && (
          <div className="bg-[hsl(var(--card))] rounded-lg border border-[hsl(var(--border))] p-6">
            <h2 className="text-xl font-semibold mb-4">Vessel Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[hsl(var(--muted-foreground))]">Vessel Name</label>
                <p className="text-[hsl(var(--foreground))]">{rfqDetails.vesselId.name}</p>
              </div>
              {rfqDetails.vesselId.imoNumber && (
                <div>
                  <label className="text-sm font-medium text-[hsl(var(--muted-foreground))]">IMO Number</label>
                  <p className="text-[hsl(var(--foreground))]">{rfqDetails.vesselId.imoNumber}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* RFQ Items */}
        {rfqItems.length > 0 && (
          <div className="bg-[hsl(var(--card))] rounded-lg border border-[hsl(var(--border))] p-6">
            <h2 className="text-xl font-semibold mb-4">RFQ Items</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[hsl(var(--border))]">
                    <th className="text-left p-3 text-sm font-semibold">Description</th>
                    <th className="text-left p-3 text-sm font-semibold">Required Qty</th>
                    <th className="text-left p-3 text-sm font-semibold">UOM</th>
                  </tr>
                </thead>
                <tbody>
                  {rfqItems.map((item, idx) => (
                    <tr key={idx} className="border-b border-[hsl(var(--border))]">
                      <td className="p-3">{item.description}</td>
                      <td className="p-3">{item.requiredQty}</td>
                      <td className="p-3">{item.uom || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Vendor Quotations List */}
        <div className="bg-[hsl(var(--card))] rounded-lg border border-[hsl(var(--border))] p-8">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
            Vendor Quotations
            <span className="bg-[hsl(var(--primary))] text-white text-xs px-2 py-0.5 rounded-full">
              {vendorQuotations.length}
            </span>
          </h2>
          {vendorQuotations.length === 0 ? (
            <div className="text-center py-8 bg-[hsl(var(--muted)/0.3)] rounded-lg border border-dashed border-[hsl(var(--border))]">
              <p className="text-[hsl(var(--muted-foreground))]">No vendor quotations received yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-[hsl(var(--border))]">
                    <th className="text-left p-3 text-sm font-semibold">Quotation #</th>
                    <th className="text-left p-3 text-sm font-semibold">Vendor</th>
                    <th className="text-left p-3 text-sm font-semibold">Amount</th>
                    <th className="text-left p-3 text-sm font-semibold">Status</th>
                    <th className="text-left p-3 text-sm font-semibold">Submitted</th>
                    <th className="text-center p-3 text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vendorQuotations.map((q) => (
                    <tr key={q._id} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted)/0.2)] transition-colors">
                      <td className="p-3 font-medium">{q.quotationNumber}</td>
                      <td className="p-3">{q.vendorOrganizationId?.name || 'Unknown Vendor'}</td>
                      <td className="p-3 font-semibold">
                        {q.currency} {Number(q.totalAmount || 0).toLocaleString()}
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          q.status === 'finalized' || q.status === 'ordered' 
                            ? 'bg-green-100 text-green-800' 
                            : q.status === 'submitted' || q.status === 'counter_received'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-[#0088cc]/10 text-[hsl(var(--foreground))]'
                        }`}>
                          {q.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="p-3 text-sm">
                        {new Date(q.submittedAt).toLocaleDateString()}
                      </td>
                      <td className="p-3 text-center">
                        {q.status === 'finalized' || q.status === 'ordered' ? (
                          <span className="text-green-600 flex items-center justify-center gap-1 text-xs font-medium">
                            <MdCheckCircle className="w-4 h-4" /> Awarded
                          </span>
                        ) : (
                          <span className="text-[hsl(var(--muted-foreground))] text-xs font-medium">
                            Awaiting Customer Award
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Submit Special Offer Section or Submitted Quotation Details */}
        <div className="bg-[hsl(var(--card))] rounded-lg border border-[hsl(var(--border))] p-8">
          {existingQuotation ? (
            // Show submitted quotation details
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-[hsl(var(--foreground))]">
                  {EUROASIANN_OFFER_LABEL} Submitted
                </h2>
                <div className="flex items-center gap-3">
                  {existingQuotation.status === 'finalized' && linkedPo?._id && (
                    <button
                      type="button"
                      onClick={() => navigate(`/dashboard/admin/pos/${linkedPo._id}`)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#0088cc] text-white hover:bg-[#0077b3]"
                    >
                      <MdShoppingCart className="w-4 h-4" />
                      Open PO {linkedPo.poNumber ? `(${linkedPo.poNumber})` : ''}
                    </button>
                  )}
                  <span className="px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    {existingQuotation.status === 'finalized' ? 'Finalized' : existingQuotation.status === 'submitted' ? 'Submitted' : existingQuotation.status}
                  </span>
                </div>
              </div>

              {/* Quotation Information */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="text-sm font-medium text-[hsl(var(--muted-foreground))] block mb-1">
                    Quotation Number
                  </label>
                  <p className="text-[hsl(var(--foreground))] font-semibold">{existingQuotation.quotationNumber}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[hsl(var(--muted-foreground))] block mb-1">
                    Title
                  </label>
                  <p className="text-[hsl(var(--foreground))]">{existingQuotation.title}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[hsl(var(--muted-foreground))] block mb-1">
                    Total Amount
                  </label>
                  <p className="text-[hsl(var(--foreground))] font-semibold text-lg">
                    {existingQuotation.currency} {Number(existingQuotation.totalAmount || 0).toFixed(2)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-[hsl(var(--muted-foreground))] block mb-1">
                    Submitted At
                  </label>
                  <p className="text-[hsl(var(--foreground))]">
                    {existingQuotation.submittedAt ? new Date(existingQuotation.submittedAt).toLocaleString() : '-'}
                  </p>
                </div>
              </div>

              {/* Quotation Items Table */}
              {existingQuotation.items && existingQuotation.items.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Quotation Items</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b border-[hsl(var(--border))]">
                          <th className="text-left p-3 text-sm font-semibold">Description</th>
                          <th className="text-left p-3 text-sm font-semibold">Required Qty</th>
                          <th className="text-left p-3 text-sm font-semibold">Quoted Price</th>
                          <th className="text-left p-3 text-sm font-semibold">Offered Qty</th>
                          <th className="text-left p-3 text-sm font-semibold">Offered Quality</th>
                        </tr>
                      </thead>
                      <tbody>
                        {existingQuotation.items.map((item, idx) => (
                          <tr key={idx} className="border-b border-[hsl(var(--border))]">
                            <td className="p-3">{item.description || '-'}</td>
                            <td className="p-3">{item.requiredQty || '-'}</td>
                            <td className="p-3">{existingQuotation.currency} {Number(item.quotedPrice || 0).toFixed(2)}</td>
                            <td className="p-3">{item.offeredQty || '-'}</td>
                            <td className="p-3">{item.offeredQuality || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Payment Details Section - Show when offer finalized and payment not yet approved */}
              {existingQuotation.status === 'finalized' && (!paymentProof || paymentProof.status !== 'approved') && (
                <div className="mt-8 pt-6 border-t border-[hsl(var(--border))]">
                  <div className="flex items-center justify-between mb-4 pb-2 border-b border-[hsl(var(--border))]">
                    <h2 className="text-xl font-semibold">
                      Payment Details
                    </h2>
                    <button
                      onClick={() => {
                        queryClient.invalidateQueries({ queryKey: ['admin-payment-proof', existingQuotation._id] });
                      }}
                      className="text-sm text-[#0088cc] dark:text-blue-400 hover:text-[#0077b3] dark:hover:text-blue-300 px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      title="Refresh payment details"
                    >
                      🔄 Refresh
                    </button>
                  </div>
                  {isLoadingPaymentProof ? (
                    <div className="p-6 bg-[#0088cc]/5 dark:bg-[#0088cc]/18/50 border border-[hsl(var(--border))] rounded-lg">
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading payment details...</p>
                    </div>
                  ) : paymentProof ? (
                    <div className="p-6 bg-blue-50 dark:bg-blue-900/20 border-2 border-[#0088cc] dark:border-[#0088cc] rounded-lg shadow-lg">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-2xl">💰</span>
                        <h3 className="text-xl font-bold text-blue-800 dark:text-blue-200">
                          Payment Proof Received
                        </h3>
                      </div>
                      <p className="text-sm text-[#0077b3] dark:text-blue-300 mb-4">
                        Customer has submitted payment proof. Please review the payment details below and approve to proceed.
                      </p>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 block mb-1">Payment Amount</label>
                          <p className="text-[hsl(var(--foreground))] dark:text-[hsl(var(--foreground))] font-semibold text-lg">
                            {paymentProof.currency || 'USD'} {Number(paymentProof.paymentAmount || 0).toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 block mb-1">Payment Date</label>
                          <p className="text-[hsl(var(--foreground))] dark:text-[hsl(var(--foreground))]">
                            {paymentProof.paymentDate ? new Date(paymentProof.paymentDate).toLocaleDateString() : '-'}
                          </p>
                        </div>
                        {paymentProof.paymentMethod && (
                          <div>
                            <label className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 block mb-1">Payment Method</label>
                            <p className="text-[hsl(var(--foreground))] dark:text-[hsl(var(--foreground))]">{paymentProof.paymentMethod}</p>
                          </div>
                        )}
                        {paymentProof.transactionReference && (
                          <div>
                            <label className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 block mb-1">Transaction Reference</label>
                            <p className="text-[hsl(var(--foreground))] dark:text-[hsl(var(--foreground))]">{paymentProof.transactionReference}</p>
                          </div>
                        )}
                        {paymentProof.notes && (
                          <div className="col-span-2">
                            <label className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 block mb-1">Notes</label>
                            <p className="text-[hsl(var(--foreground))] dark:text-[hsl(var(--foreground))]">{paymentProof.notes}</p>
                          </div>
                        )}
                      </div>
                      {paymentProof.proofDocuments && paymentProof.proofDocuments.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-800">
                          <label className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 block mb-2">
                            Payment Proof Documents
                          </label>
                          <div className="space-y-2">
                            {paymentProof.proofDocuments.map((doc: any, docIndex: number) => (
                              <div key={docIndex} className="flex items-center justify-between p-2 bg-white dark:bg-[#0088cc]/18 rounded border border-blue-200 dark:border-[#0088cc]/20">
                                <span className="text-sm text-[hsl(var(--foreground))] dark:text-[hsl(var(--foreground))]">{doc.fileName}</span>
                                {doc.fileUrl && (
                                  <FileDownloadLink
                                    href={resolveFileUrl(doc.fileUrl)}
                                    fileName={doc.fileName}
                                    className="text-[#0088cc] dark:text-blue-400 hover:text-[#0077b3] dark:hover:text-blue-300 text-sm"
                                  >
                                    View
                                  </FileDownloadLink>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {paymentProof.submittedAt && (
                        <p className="text-sm text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 mt-4">
                          Payment proof received: {new Date(paymentProof.submittedAt).toLocaleString()}
                        </p>
                      )}
                      <div className="mt-6 pt-4 border-t border-blue-200 dark:border-blue-800 flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={async () => {
                            if (window.confirm('Approve this payment and start packing? The customer will be notified.')) {
                              try {
                                const response = await authenticatedFetch(
                                  `/api/v1/admin/payment-proof/${existingQuotation._id}/approve`,
                                  { method: 'POST' }
                                );
                                if (response.ok) {
                                  alert('Payment approved! Customer has been notified.');
                                  queryClient.invalidateQueries({ queryKey: ['admin-payment-proof', existingQuotation._id] });
                                  queryClient.invalidateQueries({ queryKey: ['admin-rfq-po', id] });
                                  queryClient.invalidateQueries({ queryKey: ['admin-pos'] });
                                } else {
                                  const error = await response.json();
                                  alert(error.error || 'Failed to approve payment');
                                }
                              } catch {
                                alert('Failed to approve payment');
                              }
                            }
                          }}
                          className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <MdCheckCircle className="w-5 h-5" />
                          Approve Payment and Start Packing
                        </button>
                        <button
                          onClick={async () => {
                            const reason = window.prompt('Rejection reason (optional):') ?? '';
                            if (reason === null) return;
                            try {
                              const response = await authenticatedFetch(
                                `/api/v1/admin/payment-proof/${existingQuotation._id}/reject`,
                                {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ reason }),
                                }
                              );
                              if (response.ok) {
                                alert('Payment proof rejected. Customer can resubmit.');
                                queryClient.invalidateQueries({ queryKey: ['admin-payment-proof', existingQuotation._id] });
                              } else {
                                const error = await response.json();
                                alert(error.error || 'Failed to reject payment');
                              }
                            } catch {
                              alert('Failed to reject payment');
                            }
                          }}
                          className="px-6 py-3 border border-red-300 text-red-700 rounded-lg font-semibold hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                        >
                          <MdClose className="w-5 h-5" />
                          Reject
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-6 bg-[#0088cc]/5 dark:bg-[#0088cc]/18/50 border border-[hsl(var(--border))] rounded-lg">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                        <p className="text-sm font-medium text-[hsl(var(--foreground))] dark:text-[#0088cc]/45">
                          Awaiting Payment Proof
                        </p>
                      </div>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 mt-2">
                        Payment details will appear here once the customer submits payment proof.
                        {linkedPo?._id ? (
                          <>
                            {' '}
                            <button
                              type="button"
                              className="text-[#0088cc] font-semibold hover:underline"
                              onClick={() => navigate(`/dashboard/admin/pos/${linkedPo._id}`)}
                            >
                              View purchase order
                            </button>
                          </>
                        ) : null}
                      </p>
                    </div>
                  )}
                </div>
              )}


              {/* Payment Details Section - Show only after payment is approved */}
              {existingQuotation.status === 'finalized' && paymentProof && paymentProof.status === 'approved' && (
                <div className="mt-8 pt-6 border-t border-[hsl(var(--border))]">
                  <div className="flex items-center justify-between mb-4 pb-2 border-b border-[hsl(var(--border))]">
                    <h2 className="text-xl font-semibold">
                      Payment Details
                    </h2>
                    <div className="flex items-center gap-2">
                      {linkedPo?._id && (
                        <button
                          type="button"
                          onClick={() => navigate(`/dashboard/admin/pos/${linkedPo._id}`)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#0088cc] text-white hover:bg-[#0077b3]"
                        >
                          <MdShoppingCart className="w-4 h-4" />
                          Open PO
                        </button>
                      )}
                      {linkedShipment?._id ? (
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/dashboard/admin/shipments?id=${linkedShipment._id}&search=${encodeURIComponent(linkedPo?.poNumber || '')}`
                            )
                          }
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700"
                        >
                          <MdLocalShipping className="w-4 h-4" />
                          Open Shipment
                        </button>
                      ) : linkedPo?._id ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/dashboard/admin/pos/${linkedPo._id}`)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                        >
                          <MdLocalShipping className="w-4 h-4" />
                          Init Shipping on PO
                        </button>
                      ) : null}
                      <button
                      onClick={() => {
                        queryClient.invalidateQueries({ queryKey: ['admin-payment-proof', existingQuotation._id] });
                      }}
                      className="text-sm text-[#0088cc] dark:text-blue-400 hover:text-[#0077b3] dark:hover:text-blue-300 px-2 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      title="Refresh payment details"
                    >
                      🔄 Refresh
                    </button>
                    </div>
                  </div>
                  {isLoadingPaymentProof ? (
                    <div className="p-6 bg-[#0088cc]/5 dark:bg-[#0088cc]/18/50 border border-[hsl(var(--border))] rounded-lg">
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading payment details...</p>
                    </div>
                  ) : paymentProof ? (
                    <div className="p-6 bg-green-50 dark:bg-green-900/20 border-2 border-green-500 dark:border-green-600 rounded-lg shadow-lg">
                      <div className="flex items-center gap-3 mb-4">
                        <MdCheckCircle className="w-7 h-7 text-green-600 dark:text-green-400" />
                        <h3 className="text-xl font-bold text-green-800 dark:text-green-200">
                          💰 Payment Received
                        </h3>
                      </div>
                      <p className="text-sm text-green-700 dark:text-green-300 mb-4">
                        Customer has submitted payment proof for this {EUROASIANN_OFFER_LABEL.toLowerCase()}. Please review the payment details below.
                      </p>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 block mb-1">Payment Amount</label>
                          <p className="text-[hsl(var(--foreground))] dark:text-[hsl(var(--foreground))] font-semibold text-lg">
                            {paymentProof.currency || 'USD'} {Number(paymentProof.paymentAmount || 0).toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 block mb-1">Payment Date</label>
                          <p className="text-[hsl(var(--foreground))] dark:text-[hsl(var(--foreground))]">
                            {paymentProof.paymentDate ? new Date(paymentProof.paymentDate).toLocaleDateString() : '-'}
                          </p>
                        </div>
                        {paymentProof.paymentMethod && (
                          <div>
                            <label className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 block mb-1">Payment Method</label>
                            <p className="text-[hsl(var(--foreground))] dark:text-[hsl(var(--foreground))]">{paymentProof.paymentMethod}</p>
                          </div>
                        )}
                        {paymentProof.transactionReference && (
                          <div>
                            <label className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 block mb-1">Transaction Reference</label>
                            <p className="text-[hsl(var(--foreground))] dark:text-[hsl(var(--foreground))]">{paymentProof.transactionReference}</p>
                          </div>
                        )}
                      </div>
                      {paymentProof.proofDocuments && paymentProof.proofDocuments.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-green-200 dark:border-green-800">
                          <label className="text-sm font-medium text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 block mb-2">
                            Payment Proof Documents
                          </label>
                          <div className="space-y-2">
                            {paymentProof.proofDocuments.map((doc: any, docIndex: number) => (
                              <div key={docIndex} className="flex items-center justify-between p-2 bg-white dark:bg-[#0088cc]/18 rounded border border-green-200 dark:border-[#0088cc]/20">
                                <span className="text-sm text-[hsl(var(--foreground))] dark:text-[hsl(var(--foreground))]">{doc.fileName}</span>
                                {doc.fileUrl && (
                                  <FileDownloadLink
                                    href={resolveFileUrl(doc.fileUrl)}
                                    fileName={doc.fileName}
                                    className="text-[#0088cc] dark:text-blue-400 hover:text-[#0077b3] dark:hover:text-blue-300 text-sm"
                                  >
                                    View
                                  </FileDownloadLink>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {paymentProof.approvedAt && (
                        <div className="mt-6 pt-4 border-t border-green-200 dark:border-green-800">
                          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                              ✅ Payment Approved
                            </p>
                            {paymentProof.shippingOption ? (
                              <div className="mt-3">
                                <p className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">
                                  Shipping Decision: {paymentProof.shippingOption === 'self' ? '📦 Self Managed Shipping' : '🚢 Vendor Managed Shipping'}
                                </p>
                                {paymentProof.shippingOption === 'self' && (
                                  <div className="mt-3 p-3 bg-white dark:bg-[#0088cc]/18 rounded border border-blue-200 dark:border-[#0088cc]/20">
                                    <p className="text-xs text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 mb-1">AWB Tracking Number:</p>
                                    <p className="text-sm font-semibold text-[hsl(var(--foreground))] dark:text-[hsl(var(--foreground))] mb-3">
                                      {paymentProof.awbTrackingNumber || 'N/A'}
                                    </p>
                                    <p className="text-xs text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 mb-1">Contact Name:</p>
                                    <p className="text-sm text-[hsl(var(--foreground))] dark:text-[hsl(var(--foreground))] mb-3">
                                      {paymentProof.shippingContactName || 'N/A'}
                                    </p>
                                    <p className="text-xs text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 mb-1">Contact Email:</p>
                                    <p className="text-sm text-[hsl(var(--foreground))] dark:text-[hsl(var(--foreground))] mb-3">
                                      {paymentProof.shippingContactEmail || 'N/A'}
                                    </p>
                                    <p className="text-xs text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 mb-1">Contact Phone:</p>
                                    <p className="text-sm text-[hsl(var(--foreground))] dark:text-[hsl(var(--foreground))]">
                                      {paymentProof.shippingContactPhone || 'N/A'}
                                    </p>
                                  </div>
                                )}
                                {paymentProof.shippingOption === 'vendor-managed' && (
                                  <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                                    <p className="text-sm text-green-700 dark:text-green-300">
                                      😊 We will take care of the rest, chillax!
                                    </p>
                                    <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                                      Please proceed with arranging shipping and logistics.
                                    </p>
                                  </div>
                                )}
                                {paymentProof.shippingSelectedAt && (
                                  <p className="text-xs text-[#0088cc] dark:text-blue-400 mt-3">
                                    Selected: {new Date(paymentProof.shippingSelectedAt).toLocaleString()}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <p className="text-sm text-[#0077b3] dark:text-blue-300">
                                Waiting for customer to select shipping option...
                              </p>
                            )}
                            {paymentProof.approvedAt && (
                              <p className="text-xs text-[#0088cc] dark:text-blue-400 mt-2">
                                Approved: {new Date(paymentProof.approvedAt).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-6 bg-[#0088cc]/5 dark:bg-[#0088cc]/18/50 border border-[hsl(var(--border))] rounded-lg">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                        <p className="text-sm font-medium text-[hsl(var(--foreground))] dark:text-[#0088cc]/45">
                          Awaiting Payment Proof
                        </p>
                      </div>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65 mt-2">
                        Payment details will appear here once the customer submits payment proof. You will also receive an email notification when payment is received.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            // Show submit special offer form
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-[hsl(var(--foreground))]">
                  Submit {EUROASIANN_OFFER_LABEL}
                </h2>
                <button
                  onClick={() => setShowQuotationForm(!showQuotationForm)}
                  className="px-4 py-2 bg-[#0088cc] text-white rounded-lg hover:bg-[#0077b3] transition-colors"
                >
                  {showQuotationForm ? 'Cancel' : `Submit ${EUROASIANN_OFFER_LABEL}`}
                </button>
              </div>

              {showQuotationForm && (
                <div className="space-y-6 mt-6">
                  {/* Quotation Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Quotation Title</label>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={`${EUROASIANN_OFFER_LABEL} for ${rfqDetails.rfqNumber}`}
                        className={specialOfferFieldClass}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Currency</label>
                      <select
                        value={quotationTerms.currency || portalCurrency}
                        onChange={(e) => setQuotationTerms({ ...quotationTerms, currency: e.target.value })}
                        className={specialOfferFieldClass}
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Description</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Special offer description..."
                      rows={3}
                      className={specialOfferFieldClass}
                    />
                  </div>

                  {/* Quotation Items */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Quotation Items</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2 text-sm font-semibold">Description</th>
                            <th className="text-left p-2 text-sm font-semibold">Required Qty</th>
                            <th className="text-left p-2 text-sm font-semibold">Quoted Price</th>
                            <th className="text-left p-2 text-sm font-semibold">Offered Qty</th>
                            <th className="text-left p-2 text-sm font-semibold">Offered Quality</th>
                          </tr>
                        </thead>
                        <tbody>
                          {quotationItems.map((item, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="p-2">{item.description}</td>
                              <td className="p-2">{item.requiredQty}</td>
                              <td className="p-2">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={item.quotedPrice === '' || item.quotedPrice === 0 ? '' : item.quotedPrice}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    // Allow empty string, otherwise parse as number
                                    updateQuotationItem(idx, 'quotedPrice', value === '' ? '' : (parseFloat(value) || ''));
                                  }}
                                  placeholder="0.00"
                                  className={specialOfferTableInputClass}
                                />
                              </td>
                              <td className="p-2">
                                <input
                                  type="number"
                                  value={item.offeredQty === '' || item.offeredQty === 0 ? '' : item.offeredQty}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    // Allow empty string, otherwise parse as number
                                    updateQuotationItem(idx, 'offeredQty', value === '' ? '' : (parseInt(value) || ''));
                                  }}
                                  placeholder="0"
                                  className={specialOfferTableInputClass}
                                />
                              </td>
                              <td className="p-2">
                                <input
                                  type="text"
                                  value={item.offeredQuality}
                                  onChange={(e) => updateQuotationItem(idx, 'offeredQuality', e.target.value)}
                                  className={specialOfferTableInputClass}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Terms & Conditions */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Terms & Conditions</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Credit Type</label>
                        <select
                          value={quotationTerms.creditType || ''}
                          onChange={(e) => setQuotationTerms({ ...quotationTerms, creditType: e.target.value })}
                          className={specialOfferFieldClass}
                        >
                          <option value="">Select Credit Type</option>
                          {CREDIT_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Payment Term</label>
                        <select
                          value={
                            paymentTermCustomMode ||
                            (quotationTerms.paymentTerm &&
                              !isPresetPaymentTermOption(quotationTerms.paymentTerm))
                              ? PAYMENT_TERM_CUSTOM_VALUE
                              : quotationTerms.paymentTerm || ''
                          }
                          onChange={(e) => {
                            const term = e.target.value;
                            if (term === PAYMENT_TERM_CUSTOM_VALUE) {
                              setPaymentTermCustomMode(true);
                              if (isPresetPaymentTermOption(quotationTerms.paymentTerm)) {
                                setQuotationTerms({
                                  ...quotationTerms,
                                  paymentTerm: '',
                                  advancePercentage: 0,
                                });
                              }
                              return;
                            }
                            setPaymentTermCustomMode(false);
                            setQuotationTerms({
                              ...quotationTerms,
                              paymentTerm: term,
                              advancePercentage: advancePercentageForPaymentTerm(term),
                            });
                          }}
                          className={specialOfferFieldClass}
                        >
                          <option value="">Select Payment Term</option>
                          {PAYMENT_TERM_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                          <option value={PAYMENT_TERM_CUSTOM_VALUE}>Custom (enter your own)</option>
                        </select>
                        {(paymentTermCustomMode ||
                          (quotationTerms.paymentTerm &&
                            !isPresetPaymentTermOption(quotationTerms.paymentTerm))) && (
                          <input
                            type="text"
                            value={quotationTerms.paymentTerm || ''}
                            onChange={(e) => {
                              const term = e.target.value;
                              setQuotationTerms({
                                ...quotationTerms,
                                paymentTerm: term,
                                advancePercentage: advancePercentageForPaymentTerm(term),
                              });
                            }}
                            placeholder="e.g. 40% advance, 60% against B/L"
                            className={`${specialOfferFieldClass} mt-2`}
                          />
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Insurance Term</label>
                        <select
                          value={quotationTerms.insuranceTerm || ''}
                          onChange={(e) => setQuotationTerms({ ...quotationTerms, insuranceTerm: e.target.value })}
                          className={specialOfferFieldClass}
                        >
                          <option value="">Select Insurance</option>
                          {INSURANCE_TERM_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Tax Term</label>
                        <select
                          value={quotationTerms.taxTerm || ''}
                          onChange={(e) => setQuotationTerms({ ...quotationTerms, taxTerm: e.target.value })}
                          className={specialOfferFieldClass}
                        >
                          <option value="">Select Tax Term</option>
                          {TAX_TERM_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Transport Term</label>
                        <select
                          value={quotationTerms.transportTerm || ''}
                          onChange={(e) => setQuotationTerms({ ...quotationTerms, transportTerm: e.target.value })}
                          className={specialOfferFieldClass}
                        >
                          <option value="">Select Transport</option>
                          {TRANSPORT_TERM_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Delivery Term</label>
                        <select
                          value={quotationTerms.deliveryTerm || ''}
                          onChange={(e) => setQuotationTerms({ ...quotationTerms, deliveryTerm: e.target.value })}
                          className={specialOfferFieldClass}
                        >
                          <option value="">Select Delivery Term</option>
                          {DELIVERY_TERM_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Packing Term</label>
                        <select
                          value={quotationTerms.packingTerm || ''}
                          onChange={(e) => setQuotationTerms({ ...quotationTerms, packingTerm: e.target.value })}
                          className={specialOfferFieldClass}
                        >
                          <option value="">Select Packing Term</option>
                          {PACKING_TERM_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end gap-4 pt-4 border-t">
                    <button
                      onClick={() => setShowQuotationForm(false)}
                      className="px-6 py-2 border rounded-lg hover:bg-[#0088cc]/10 dark:hover:bg-[#0088cc]/18 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmitQuotation}
                      disabled={submitQuotationMutation.isPending}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitQuotationMutation.isPending ? 'Submitting...' : `Submit ${EUROASIANN_OFFER_LABEL}`}
                    </button>
                  </div>

                  {submitQuotationMutation.isError && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-sm text-red-600 dark:text-red-400">
                        Error: {submitQuotationMutation.error instanceof Error ? submitQuotationMutation.error.message : 'Failed to submit quotation'}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}




